/** URL ⇄ ReportFilterState (and the explorer's metric selection). Keeps every view linkable. */
import {
  DEFAULT_FILTER_STATE,
  GRANULARITIES,
  RANGE_PRESETS,
  type ChartKind,
  type ReportFilterState,
} from "./types";

export interface ReportSearch {
  range?: string;
  from?: string;
  to?: string;
  compare?: string;
  granularity?: string;
  f?: string; // JSON {dim: [values]}
  metrics?: string; // csv (explorer)
  group_by?: string; // csv (explorer)
  chart?: string; // explorer
  report?: string; // saved report slug (explorer)
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

export function validateReportSearch(search: Record<string, unknown>): ReportSearch {
  const out: ReportSearch = {};
  const range = str(search.range);
  if (range && (range === "custom" || RANGE_PRESETS.some((p) => p.key === range)))
    out.range = range;
  if (DAY.test(str(search.from) ?? "")) out.from = str(search.from);
  if (DAY.test(str(search.to) ?? "")) out.to = str(search.to);
  const compare = str(search.compare);
  if (compare && ["none", "previous_period", "previous_year"].includes(compare))
    out.compare = compare;
  const g = str(search.granularity);
  if (g && GRANULARITIES.some((x) => x.key === g)) out.granularity = g;
  if (str(search.f)) out.f = str(search.f);
  if (str(search.metrics)) out.metrics = str(search.metrics);
  if (str(search.group_by)) out.group_by = str(search.group_by);
  const chart = str(search.chart);
  if (chart && ["line", "bar", "stacked", "table"].includes(chart)) out.chart = chart;
  if (str(search.report)) out.report = str(search.report);
  return out;
}

export function filterStateFromSearch(s: ReportSearch): ReportFilterState {
  let filters: Record<string, string[]> = {};
  if (s.f) {
    try {
      const parsed = JSON.parse(s.f) as unknown;
      if (parsed && typeof parsed === "object")
        filters = Object.fromEntries(
          Object.entries(parsed as Record<string, unknown>)
            .filter(([, v]) => Array.isArray(v))
            .map(([k, v]) => [
              k,
              (v as unknown[]).filter((x): x is string => typeof x === "string"),
            ]),
        );
    } catch {
      /* ignore a bad f param */
    }
  }
  return {
    range: (s.range as ReportFilterState["range"]) ?? DEFAULT_FILTER_STATE.range,
    from: s.from,
    to: s.to,
    compare: (s.compare as ReportFilterState["compare"]) ?? DEFAULT_FILTER_STATE.compare,
    granularity:
      (s.granularity as ReportFilterState["granularity"]) ?? DEFAULT_FILTER_STATE.granularity,
    filters,
  };
}

export function searchFromFilterState(
  st: ReportFilterState,
  extra: Partial<ReportSearch> = {},
): ReportSearch {
  const active = Object.fromEntries(Object.entries(st.filters).filter(([, v]) => v.length));
  return {
    range: st.range === DEFAULT_FILTER_STATE.range ? undefined : st.range,
    from: st.range === "custom" ? st.from : undefined,
    to: st.range === "custom" ? st.to : undefined,
    compare: st.compare === DEFAULT_FILTER_STATE.compare ? undefined : st.compare,
    granularity: st.granularity === DEFAULT_FILTER_STATE.granularity ? undefined : st.granularity,
    f: Object.keys(active).length ? JSON.stringify(active) : undefined,
    ...extra,
  };
}

export function chartFromSearch(s: ReportSearch): ChartKind | undefined {
  return s.chart as ChartKind | undefined;
}
